/**
 * Tenant-scoped audit view assembly (PR #533 review, Codex P2 — WP-575, TEN-2917).
 *
 * "Resolve the calling workspace, filter the audit log to it, pull that workspace's counter
 * slice, and format the summary" became a real responsibility of its own once the audit seam
 * had to be tenant-scoped: HTTP transport multiplexes many tenants through one MCP process
 * (client.ts's per-key `state()`), so `workspace action=audit` reading the log/counters
 * unfiltered would hand any authenticated caller every OTHER tenant's call history and failure
 * rates. That is a cohesive unit that does not belong in a tool-dispatch file — extracted out
 * of tools/health.ts (not inlined there) for the same reason lib/toolActionCounts.ts was pulled
 * out of client.ts: both files are grandfathered shrink-only under the module-health ratchet
 * (STD-2/DEC-1504), so a genuine new responsibility gets its own module instead of widening the
 * baseline.
 */
// The seam owns the counters and the audit buffer, so take them from the seam directly rather
// than through client.ts's re-export — client.ts is grandfathered shrink-only under the
// module-health ratchet, and a re-export line added purely to route this import through it
// would grow that file for no benefit.
import {
  formatGatewaySeamSummary,
  getAuditLog,
  getMergedGatewaySeamCounters,
  type AuditEntry,
  type GatewaySeamCounters,
} from "../gatewaySeam.js";
// Caller identity is client.ts's own state, not the seam's.
import { cacheScope, getWorkspaceId } from "../client.js";

export interface TenantAuditView {
  /** The resolved (or fail-closed fallback) workspace this view is scoped to. */
  workspace: string;
  /** This workspace's slice of the process-lifetime audit buffer, oldest first. */
  log: readonly AuditEntry[];
  /** The last `limit` entries of `log`. */
  recent: readonly AuditEntry[];
  /** This workspace's slice of the gateway seam counters. */
  gatewaySeam: GatewaySeamCounters;
  /** Pre-formatted `# Audit Log (last N of M total)` header + one line per `recent` entry. */
  logLines: string[];
  /** `formatGatewaySeamSummary(gatewaySeam)` — the human-readable seam section. */
  seamSummaryText: string;
}

/**
 * Resolve who's asking, then build their (and only their) audit view.
 *
 * Fail closed: if the workspace can't be resolved, fall back to `cacheScope()` (hashed
 * per-API-key in HTTP mode, "stdio" in stdio mode — client.ts) rather than a shared literal like
 * "unresolved", which would itself be a (narrower) cross-tenant collision between two different
 * not-yet-resolved callers. Either way, never fall through to the unfiltered global log/counters.
 */
export async function buildTenantAuditView(limit: number): Promise<TenantAuditView> {
  const scope = cacheScope();
  let workspace: string;
  try {
    workspace = await getWorkspaceId();
  } catch {
    workspace = scope;
  }

  // BOTH buckets, because this caller's calls are recorded under both. `audit()` attributes a
  // call to `state().workspaceId ?? cacheScope()` (client.ts), and `workspaceId` is only set
  // once `resolveWorkspace` RETURNS — so every session's FIRST call is filed under
  // `cacheScope()`. Scoping to the resolved workspaceId alone hid that call forever and
  // under-counted every tenant's view of ITSELF, in stdio mode too. Safe because `cacheScope()`
  // is derived from this caller's own API key: both buckets are the same tenant, so this widens
  // the caller's view of itself and still cannot reach another tenant's.
  const ownScopes = [workspace, scope];
  // Scoped at the SOURCE, not filtered after the fact (PR #533 review, Codex P2). Filtering a
  // shared buffer here was too late: it holds the last N calls made by ANY tenant, so a noisy
  // neighbour had already evicted this caller's entries before this line ran, and a tenant
  // with real recorded timeouts saw an empty log. The seam now keeps a bounded buffer per
  // workspace, so `limit` is a floor this caller can rely on rather than a share of a pool.
  const log = getAuditLog(ownScopes);
  const recent = log.slice(-limit);
  const gatewaySeam = getMergedGatewaySeamCounters(ownScopes);

  const logLines = [
    `# Audit Log (last ${recent.length} of ${log.length} total)\n`,
    ...recent.map((entry) => {
      const toolPart = entry.toolContext
        ? ` [${entry.toolContext.tool}${entry.toolContext.action ? ` action=${entry.toolContext.action}` : ""}]`
        : "";
      const errPart = entry.error ? ` — ${entry.error}` : "";
      return `${entry.status === "ok" ? "✓" : "✗"} \`${entry.fn}\`${toolPart} ${entry.durationMs}ms ${entry.status}${errPart}`;
    }),
  ];

  return { workspace, log, recent, gatewaySeam, logLines, seamSummaryText: formatGatewaySeamSummary(gatewaySeam) };
}
